import * as tf from '@tensorflow/tfjs';

/**
 * AI-Powered Housing Recommendation Engine
 * Uses a logistic regression model trained on synthetic health assessment data
 * to recommend PDL housing assignments (dormitory, isolation, or hospitalization)
 * 
 * Technology: TensorFlow.js - Browser-based Machine Learning
 * Model Type: Multinomial Logistic Regression (Softmax Regression)
 * Task Type: Multi-class Classification (3 classes)
 * 
 * LOGISTIC REGRESSION MODEL:
 * - Simple, interpretable linear model
 * - Direct input → output mapping (no hidden layers)
 * - Uses softmax activation for multi-class classification
 * - Faster training and prediction than deep neural networks
 * - Fewer parameters (45 vs 1000+) = less overfitting risk
 */

class AIRecommendationEngine {
  constructor() {
    this.model = null;
    this.isModelLoaded = false;
    this.trainingData = [];
    this.labels = [];
  }

  /**
   * Initialize and train the model
   */
  async initialize() {
    console.log('🤖 AI Recommendation Engine - Initializing...');
    console.log('📊 Model Type: Multinomial Logistic Regression');
    
    // Create a logistic regression model
    // Simple architecture: Input → Output (NO hidden layers)
    this.model = tf.sequential({
      layers: [
        // Single layer: Direct input to output mapping
        // This is logistic regression for multi-class classification
        tf.layers.dense({ 
          inputShape: [15],        // 15 health features as input
          units: 3,                // 3 output classes (dormitory, isolation, hospitalization)
          activation: 'softmax',   // Softmax converts to probabilities
          kernelInitializer: 'glorotUniform',  // Xavier initialization
          useBias: true            // Include bias term (like y-intercept)
        })
      ]
    });

    // Compile the model
    this.model.compile({
      optimizer: tf.train.adam(0.01), // Higher learning rate for simpler model
      loss: 'categoricalCrossentropy',  // Standard for multi-class classification
      metrics: ['accuracy']
    });

    console.log('📊 Logistic Regression Model Architecture:');
    console.log('   Input Features: 15 (health assessment data)');
    console.log('   Output Classes: 3 (dormitory, isolation, hospitalization)');
    console.log('   Total Parameters: 48 (15 features × 3 classes + 3 biases)');
    console.log('   Hidden Layers: 0 (pure logistic regression)');
    this.model.summary();

    // Load pre-trained weights if available, or train with synthetic data
    await this.loadOrTrainModel();
    
    this.isModelLoaded = true;
    console.log('✅ AI Recommendation Engine - Ready');
  }

  /**
   * Load pre-trained model or train with synthetic data
   */
  async loadOrTrainModel() {
    try {
      // Try to load saved model from localStorage
      this.model = await tf.loadLayersModel('localstorage://hr-ai-model');
      console.log('📦 Loaded pre-trained model from browser storage');
      return;
    } catch (error) {
      console.log('🔧 No saved model found. Training new model with synthetic data...');
    }

    // Generate synthetic training data based on medical rules
    const trainingData = this.generateTrainingData();
    
    // Train the model
    await this.trainModel(trainingData.features, trainingData.labels);
    
    // Save the trained model
    await this.model.save('localstorage://hr-ai-model');
    console.log('💾 Model trained and saved to browser storage');
  }

  /**
   * Generate synthetic training data based on medical decision rules
   * Creates 1000 realistic training examples
   */
  generateTrainingData() {
    const features = [];
    const labels = [];

    console.log('🔬 Generating 1000 synthetic training examples...');

    // Generate 1000 training examples
    for (let i = 0; i < 1000; i++) {
      const sample = this.generateSample();
      features.push(sample.features);
      labels.push(sample.label);
    }

    console.log('📈 Training data distribution:');
    const dormCount = labels.filter(l => l[0] === 1).length;
    const isoCount = labels.filter(l => l[1] === 1).length;
    const hospCount = labels.filter(l => l[2] === 1).length;
    console.log(`   - Dormitory: ${dormCount} (${(dormCount/10).toFixed(1)}%)`);
    console.log(`   - Isolated: ${isoCount} (${(isoCount/10).toFixed(1)}%)`);
    console.log(`   - Hospitalized: ${hospCount} (${(hospCount/10).toFixed(1)}%)`);

    return {
      features: tf.tensor2d(features),
      labels: tf.tensor2d(labels)
    };
  }

  /**
   * Generate a single training sample
   * Uses realistic medical probabilities and decision rules
   */
  generateSample() {
    // Feature vector (15 features):
    // [0] TB screening positive (0-1)
    // [1] Fever present (0-1)
    // [2] Abnormal vital signs (0-1)
    // [3] Mental health issues (0-1)
    // [4] Psychiatric history (0-1)
    // [5] ARV treatment (0-1)
    // [6] Drug use severity (0-1)
    // [7] Physical exam abnormalities count (0-1 normalized)
    // [8] BMI abnormal (0-1)
    // [9] Communicable disease risk (0-1)
    // [10] Injury/allegation (0-1)
    // [11] Age factor (0-1 normalized)
    // [12] Chronic condition (0-1)
    // [13] Recent hospitalization (0-1)
    // [14] Requires special monitoring (0-1)

    const features = [];
    
    // Randomly generate features with realistic distributions
    const tbPositive = Math.random() < 0.15 ? 1 : 0; // 15% TB positive rate
    const fever = Math.random() < 0.2 ? 1 : 0; // 20% with fever
    const abnormalVitals = Math.random() < 0.25 ? 1 : 0; // 25% abnormal vitals
    const mentalHealth = Math.random() < 0.18 ? 1 : 0; // 18% mental health issues
    const psychHistory = Math.random() < 0.12 ? 1 : 0; // 12% psychiatric history
    const arvTreatment = Math.random() < 0.08 ? 1 : 0; // 8% on ARV
    const drugSeverity = Math.random(); // Continuous 0-1
    const physicalAbnormalities = Math.random(); // Continuous 0-1
    const bmiAbnormal = Math.random() < 0.3 ? 1 : 0; // 30% abnormal BMI
    const communicableRisk = Math.random() < 0.1 ? 1 : 0; // 10% communicable disease
    const injury = Math.random() < 0.05 ? 1 : 0; // 5% alleged ill-treatment
    const ageFactor = Math.random(); // Normalized age
    const chronicCondition = Math.random() < 0.22 ? 1 : 0; // 22% chronic conditions
    const recentHospital = Math.random() < 0.06 ? 1 : 0; // 6% recent hospitalization
    const specialMonitoring = Math.random() < 0.15 ? 1 : 0; // 15% special monitoring

    features.push(
      tbPositive, fever, abnormalVitals, mentalHealth, psychHistory,
      arvTreatment, drugSeverity, physicalAbnormalities, bmiAbnormal,
      communicableRisk, injury, ageFactor, chronicCondition,
      recentHospital, specialMonitoring
    );

    // Determine label based on features (rule-based logic for training)
    let label;
    
    // HOSPITALIZED (one-hot: [0, 0, 1])
    // Critical conditions requiring immediate medical care
    if (
      (tbPositive && fever) ||                    // TB with fever = emergency
      (abnormalVitals && fever) ||                // Vital signs + fever = danger
      recentHospital ||                           // Recently hospitalized
      (communicableRisk && fever) ||              // Infectious + fever
      (physicalAbnormalities > 0.7) ||            // Severe physical issues
      arvTreatment                                // ARV needs medical monitoring
    ) {
      label = [0, 0, 1]; // hospitalized
    }
    // ISOLATED (one-hot: [0, 1, 0])
    // Conditions requiring separation but not hospitalization
    else if (
      tbPositive ||                               // TB requires isolation
      communicableRisk ||                         // Contagious disease
      (mentalHealth && psychHistory) ||           // Mental health crisis
      injury ||                                   // Alleged ill-treatment documentation
      (drugSeverity > 0.7) ||                     // Severe drug withdrawal risk
      specialMonitoring                           // Requires special observation
    ) {
      label = [0, 1, 0]; // isolated
    }
    // DORMITORY (one-hot: [1, 0, 0])
    // Low risk, can be in general population
    else {
      label = [1, 0, 0]; // dormitory
    }

    return { features, label };
  }

  /**
   * Train the model with provided data
   */
  async trainModel(features, labels) {
    console.log('🎓 Training Logistic Regression Model...');
    console.log('   Algorithm: Gradient Descent with Adam Optimizer');
    console.log('   Training Method: Iterative weight optimization');
    
    const history = await this.model.fit(features, labels, {
      epochs: 100,             // More epochs for simpler model to converge
      batchSize: 64,           // Larger batch size for stable gradients
      validationSplit: 0.2,    // Use 20% for validation
      shuffle: true,           // Randomize training order
      callbacks: {
        onEpochEnd: (epoch, logs) => {
          if (epoch % 20 === 0) {
            console.log(
              `   Epoch ${epoch + 1}/100: ` +
              `loss=${logs.loss.toFixed(4)}, ` +
              `accuracy=${(logs.acc * 100).toFixed(1)}%, ` +
              `val_accuracy=${(logs.val_acc * 100).toFixed(1)}%`
            );
          }
        }
      }
    });

    console.log('🎯 Training completed!');
    const finalAccuracy = history.history.acc[history.history.acc.length - 1];
    const finalValAccuracy = history.history.val_acc[history.history.val_acc.length - 1];
    console.log(`   Final training accuracy: ${(finalAccuracy * 100).toFixed(1)}%`);
    console.log(`   Final validation accuracy: ${(finalValAccuracy * 100).toFixed(1)}%`);
    console.log('   Model Type: Linear Decision Boundaries');

    return history;
  }

  /**
   * Extract features from form data
   * Converts PDL health record to 15 numerical features
   */
  extractFeatures(formState) {
    const features = [];

    // Feature 0: TB screening positive
    const tbPositive = formState.tb?.remarks?.presumptiveTB || 
                      formState.tb?.remarks?.presumptiveDRTB ? 1 : 0;
    features.push(tbPositive);

    // Feature 1: Fever present
    const temp = parseFloat(formState.physical?.vitals?.temperature);
    const fever = (temp && temp >= 38) ? 1 : 0;
    features.push(fever);

    // Feature 2: Abnormal vital signs
    const vitals = formState.physical?.vitals || {};
    let abnormalVitals = 0;
    if (vitals.bp) {
      const bpParts = vitals.bp.split('/');
      if (bpParts.length === 2) {
        const systolic = parseInt(bpParts[0]);
        const diastolic = parseInt(bpParts[1]);
        if (systolic >= 140 || diastolic >= 90 || systolic < 90 || diastolic < 60) {
          abnormalVitals = 1;
        }
      }
    }
    features.push(abnormalVitals);

    // Feature 3: Mental health issues
    const mentalHealth = formState.assessment?.mentalEmotionalProblems?.trim() ? 1 : 0;
    features.push(mentalHealth);

    // Feature 4: Psychiatric history
    const psychHistory = formState.psychiatric?.confinementInPsychiatricFacility === 'Yes' ? 1 : 0;
    features.push(psychHistory);

    // Feature 5: ARV treatment
    const arvTreatment = formState.psychiatric?.arvTreatment === 'Yes' ? 1 : 0;
    features.push(arvTreatment);

    // Feature 6: Drug use severity (0-1 scale)
    let drugSeverity = 0;
    if (formState.drugs?.useOfIllicitDrugs === 'Yes') {
      drugSeverity = 0.3;
      if (formState.drugs?.injectingDrugs === 'Yes') drugSeverity += 0.4;
      if (formState.drugs?.multipleDrugsAtOnce === 'Yes') drugSeverity += 0.3;
    }
    features.push(Math.min(1, drugSeverity));

    // Feature 7: Physical exam abnormalities (normalized)
    const findings = formState.physical?.findings || [];
    const abnormalCount = findings.filter(f => f.value === false).length;
    const abnormalityRate = findings.length > 0 ? abnormalCount / 9 : 0; // First 9 findings
    features.push(abnormalityRate);

    // Feature 8: BMI abnormal
    const bmi = parseFloat(formState.physical?.vitals?.bmi);
    const bmiAbnormal = (bmi && (bmi < 18.5 || bmi > 30)) ? 1 : 0;
    features.push(bmiAbnormal);

    // Feature 9: Communicable disease risk
    const communicableRisk = (
      formState.tb?.checklist?.D === true || // X-ray suggestive of TB
      formState.tb?.remarks?.presumptiveTB ||
      formState.tb?.remarks?.presumptiveDRTB
    ) ? 1 : 0;
    features.push(communicableRisk);

    // Feature 10: Injury/allegation
    const injury = formState.tb_conclusion?.furtherExam?.allegation ? 1 : 0;
    features.push(injury);

    // Feature 11: Age factor (normalized, assuming age 18-80 range)
    const dob = formState.basic?.dateOfBirth;
    let ageFactor = 0.5; // default middle age
    if (dob) {
      const birthDate = new Date(dob);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      ageFactor = Math.max(0, Math.min(1, (age - 18) / 62)); // Normalize to 0-1
    }
    features.push(ageFactor);

    // Feature 12: Chronic condition
    const chronicCondition = (
      formState.assessment?.majorAdultIllnesses?.trim() ||
      formState.assessment?.familyHistory?.diabetes ||
      formState.assessment?.familyHistory?.hypertension
    ) ? 1 : 0;
    features.push(chronicCondition);

    // Feature 13: Recent hospitalization (inferred from surgical history)
    const recentHospital = formState.assessment?.majorSurgeries?.trim() ? 1 : 0;
    features.push(recentHospital);

    // Feature 14: Requires special monitoring
    const specialMonitoring = (
      formState.tb_conclusion?.furtherExam?.neuro ||
      formState.tb_conclusion?.furtherExam?.philpen ||
      formState.tb_conclusion?.furtherExam?.sbirt
    ) ? 1 : 0;
    features.push(specialMonitoring);

    return features;
  }

  /**
   * Check if form data has enough meaningful information for a reliable prediction.
   * Returns a score 0-1 representing how complete the form data is.
   * A minimum threshold of filled sections/fields is required.
   */
  checkDataSufficiency(formState) {
    let filledSections = 0;
    let totalChecks = 0;

    // Check basic info (date of birth)
    totalChecks++;
    if (formState.basic?.dateOfBirth) filledSections++;

    // Check physical exam vitals (at least temperature, BP, or BMI filled)
    totalChecks++;
    const vitals = formState.physical?.vitals || {};
    if (vitals.temperature || vitals.bp || vitals.bmi || vitals.hr || vitals.rr || vitals.height || vitals.weight) {
      filledSections++;
    }

    // Check physical exam findings (at least one finding answered)
    totalChecks++;
    const findings = formState.physical?.findings || [];
    if (findings.some(f => f.value === true || f.value === false)) filledSections++;

    // Check TB screening (at least one checklist item answered)
    totalChecks++;
    const checklist = formState.tb?.checklist || {};
    if (Object.values(checklist).some(v => v === true || v === false)) filledSections++;

    // Check medical history (at least one field filled)
    totalChecks++;
    const assessment = formState.assessment || {};
    if (
      (assessment.presentHealthConditions && assessment.presentHealthConditions.length > 0) ||
      assessment.majorAdultIllnesses?.trim() ||
      assessment.majorSurgeries?.trim() ||
      assessment.medicationsHistory?.trim() ||
      assessment.mentalEmotionalProblems?.trim() ||
      assessment.bloodType
    ) {
      filledSections++;
    }

    // Check psychiatric history (at least one field answered)
    totalChecks++;
    const psych = formState.psychiatric || {};
    if (
      psych.consultationWithPsychiatrist === 'Yes' || psych.consultationWithPsychiatrist === 'No' ||
      psych.confinementInPsychiatricFacility === 'Yes' || psych.confinementInPsychiatricFacility === 'No' ||
      psych.arvTreatment === 'Yes' || psych.arvTreatment === 'No'
    ) {
      filledSections++;
    }

    // Check substance use history (at least one field answered)
    totalChecks++;
    const drugs = formState.drugs || {};
    if (
      drugs.alcoholDrinker === 'Yes' || drugs.alcoholDrinker === 'No' ||
      drugs.smokingStatus === 'Yes' || drugs.smokingStatus === 'No' ||
      drugs.useOfIllicitDrugs === 'Yes' || drugs.useOfIllicitDrugs === 'No'
    ) {
      filledSections++;
    }

    return {
      score: totalChecks > 0 ? filledSections / totalChecks : 0,
      filledSections,
      totalChecks
    };
  }

  /**
   * Get AI recommendation with confidence scores
   * 
   * HOW LOGISTIC REGRESSION MAKES PREDICTIONS:
   * 1. Takes 15 input features (health data)
   * 2. Multiplies each feature by learned weights (coefficients)
   * 3. Adds all weighted features together + bias term
   * 4. Applies softmax function to convert to probabilities (0-100%)
   * 5. Returns class with highest probability
   * 
   * Mathematical Formula (for each class):
   * score_class = w1*f1 + w2*f2 + ... + w15*f15 + bias
   * probability_class = exp(score_class) / sum(exp(all_scores))
   * 
   * This is a LINEAR model - it draws straight decision boundaries
   * between the three classes (dormitory, isolation, hospitalization)
   */
  async predict(formState) {
    if (!this.isModelLoaded) {
      await this.initialize();
    }

    // Check data sufficiency before making prediction
    const sufficiency = this.checkDataSufficiency(formState);
    const MINIMUM_THRESHOLD = 0.4; // At least ~3 out of 7 sections must have data

    if (sufficiency.score < MINIMUM_THRESHOLD) {
      console.log(`⚠️ Insufficient data for AI prediction (${sufficiency.filledSections}/${sufficiency.totalChecks} sections filled)`);
      return {
        insufficientData: true,
        filledSections: sufficiency.filledSections,
        totalChecks: sufficiency.totalChecks,
        score: sufficiency.score
      };
    }

    // Extract features
    const features = this.extractFeatures(formState);
    
    console.log('🔮 Logistic Regression Prediction:');
    console.log('   Input Features:', features);
    
    // Make prediction using logistic regression
    const inputTensor = tf.tensor2d([features]);
    const prediction = this.model.predict(inputTensor);
    const probabilities = await prediction.data();
    
    // Clean up tensors to prevent memory leaks
    inputTensor.dispose();
    prediction.dispose();

    // Map probabilities to recommendations
    const recommendations = [
      { type: 'dorm', label: 'General Dormitory', probability: probabilities[0] },
      { type: 'isolated', label: 'Isolation', probability: probabilities[1] },
      { type: 'hospitalized', label: 'Hospitalization', probability: probabilities[2] }
    ];

    // Sort by probability (highest first)
    recommendations.sort((a, b) => b.probability - a.probability);

    // Get top recommendation
    const topRecommendation = recommendations[0];

    // Determine confidence level
    let confidence = 'High';
    if (topRecommendation.probability < 0.6) confidence = 'Low';
    else if (topRecommendation.probability < 0.8) confidence = 'Medium';

    // Generate reasoning based on top features
    const reasoning = this.generateAIReasoning(features, topRecommendation);

    console.log(`✨ AI Recommendation: ${topRecommendation.type.toUpperCase()}`);
    console.log(`   Probability: ${(topRecommendation.probability * 100).toFixed(1)}%`);
    console.log(`   Model: Multinomial Logistic Regression`);

    return {
      recommendation: topRecommendation.type,
      confidence,
      confidenceScore: (topRecommendation.probability * 100).toFixed(1),
      allProbabilities: recommendations,
      reasoning,
      features: this.getFeatureImportance(features)
    };
  }

  /**
   * Generate AI reasoning based on features
   * Logistic regression uses linear combinations of features
   */
  generateAIReasoning(features, topRecommendation) {
    const reasoning = [];
    const featureNames = [
      'TB screening result',
      'Fever presence',
      'Vital signs abnormality',
      'Mental health concerns',
      'Psychiatric history',
      'ARV treatment',
      'Substance use severity',
      'Physical examination findings',
      'BMI status',
      'Communicable disease risk',
      'Injury/allegation documentation',
      'Age factor',
      'Chronic conditions',
      'Recent hospitalization',
      'Special monitoring needs'
    ];

    reasoning.push(`Logistic Regression Model Confidence: ${(topRecommendation.probability * 100).toFixed(1)}%`);
    reasoning.push('');
    reasoning.push('Key Risk Factors Detected (Linear Analysis):');

    let factorCount = 0;
    // Highlight top contributing features
    features.forEach((value, index) => {
      if (value > 0.5) {
        const severity = value > 0.9 ? 'High' : value > 0.7 ? 'Moderate-High' : 'Moderate';
        reasoning.push(`• ${featureNames[index]}: ${severity} risk`);
        factorCount++;
      }
    });

    if (factorCount === 0) {
      reasoning.push('• No significant risk factors detected');
      reasoning.push('• PDL appears healthy for general population');
    }

    return reasoning;
  }

  /**
   * Get feature importance for explanation
   */
  getFeatureImportance(features) {
    const featureNames = [
      'TB Screening', 'Fever', 'Vital Signs', 'Mental Health', 
      'Psychiatric History', 'ARV Treatment', 'Drug Use', 
      'Physical Findings', 'BMI', 'Communicable Disease',
      'Injury', 'Age', 'Chronic Condition', 'Hospitalization',
      'Special Monitoring'
    ];

    return features.map((value, index) => ({
      name: featureNames[index],
      value: value,
      importance: value > 0.7 ? 'High' : value > 0.3 ? 'Medium' : 'Low'
    })).filter(f => f.value > 0);
  }

  /**
   * Retrain model with new data (for continuous learning)
   * This allows the AI to improve from real-world feedback
   */
  async retrainWithFeedback(formState, actualOutcome) {
    // Extract features and create label from actual outcome
    const features = this.extractFeatures(formState);
    let label;
    if (actualOutcome === 'dorm') label = [1, 0, 0];
    else if (actualOutcome === 'isolated') label = [0, 1, 0];
    else if (actualOutcome === 'hospitalized') label = [0, 0, 1];

    // Add to training data
    this.trainingData.push(features);
    this.labels.push(label);

    console.log(`📝 Feedback recorded: ${this.trainingData.length} samples collected`);

    // Retrain periodically (e.g., every 10 new samples)
    if (this.trainingData.length >= 10) {
      console.log('🔄 Retraining model with user feedback...');
      
      const featuresTensor = tf.tensor2d(this.trainingData);
      const labelsTensor = tf.tensor2d(this.labels);
      
      await this.trainModel(featuresTensor, labelsTensor);
      await this.model.save('localstorage://hr-ai-model');
      
      // Clear temporary training data
      this.trainingData = [];
      this.labels = [];
      
      console.log('✅ Model retrained with user feedback');
    }
  }

  /**
   * Reset the model (clear saved weights)
   */
  async resetModel() {
    try {
      await tf.io.removeModel('localstorage://hr-ai-model');
      console.log('🗑️ Model cleared from storage');
      this.isModelLoaded = false;
      await this.initialize();
    } catch (error) {
      console.error('Error resetting model:', error);
    }
  }
}

// Create singleton instance
const aiEngine = new AIRecommendationEngine();

// Helper function to get recommendation label
export const getRecommendationLabel = (key) => {
  const labels = {
    'dorm': 'To be accommodated in general dormitory',
    'isolated': 'To be isolated',
    'hospitalized': 'To be hospitalized'
  };
  return labels[key] || labels.dorm;
};

export default aiEngine;
